import type { ForwardRefRenderFunction } from "react"
import { forwardRef, useImperativeHandle, useCallback } from "react"
import classNames from "classnames"
import { useSetState } from "ahooks-v2"
import { Checkbox, Tag, message } from "antd"
import "./index.scss"
// ==============================================
const { CheckableTag } = Tag
// ==============================================
interface stateVO {
  // 是否全部
  checkAll: boolean
  // 已选择的选项
  selectedOptions: string[]
}
interface UnlimitedChoiceProps {
  // 选项
  options: any[]
  // 选项class
  optionClass?: string
  // 是否多选
  multiple?: boolean
  // 是否必须选择一项
  isMustChoice?: boolean
  // 最多可选项数量
  maxCount?: number
  // 默认选项
  defaultValue?: string[]
  // 名称字段
  nameField?: string
  // id字段
  idField?: string
  // 不限选项value
  unlimitedValue?: string
  // 不限选项name
  unlimitedName?: string
  // 提示信息标题
  title?: string
  // 使用的组件元素类型：tag, checkbox
  elementType?: string
  // 使用的颜色类型: blue, green
  colorType?: string
  // 禁用
  disabled?: boolean
  // 数据change
  onChange?: (data: string[]) => void
}
const UnlimitedChoice: ForwardRefRenderFunction<any, UnlimitedChoiceProps> = (
  {
    options = [],
    optionClass = "",
    multiple = true,
    isMustChoice = true,
    maxCount = 0,
    nameField = "name",
    idField = "id",
    unlimitedValue = "ALL",
    unlimitedName = "不限",
    defaultValue = [unlimitedValue],
    title = "",
    elementType = "checkbox",
    colorType = "blue",
    disabled = false,
    onChange,
  },
  ref: any,
) => {
  const [state, set] = useSetState<stateVO>({
    checkAll: defaultValue.includes(unlimitedValue),
    selectedOptions: defaultValue,
  })
  const { checkAll, selectedOptions } = state
  // tag class
  const tagClassName = colorType === "green" ? "theme-tag-checkable2" : "theme-tag-checkable"

  // 全部
  const onCheckAllChange = useCallback(
    (checked) => {
      const options = checked ? [unlimitedValue] : []
      if (!checked && isMustChoice) {
        return message.error(`${title}至少选择一项`)
      }
      set({
        selectedOptions: options,
        checkAll: checked,
      })
      // 数据change
      onChange && onChange(options)
    },
    [title, unlimitedValue, isMustChoice, set, onChange],
  )

  // 选项change
  const optionsChange = useCallback(
    (type, checked) => {
      let cloneOptions: string[] = selectedOptions?.slice() || []
      const index = cloneOptions?.indexOf(unlimitedValue)
      // 删除不限
      if (index >= 0) {
        cloneOptions.splice(index, 1)
      }
      if (!checked && cloneOptions?.length === 1 && isMustChoice) {
        return message.error(`${title}至少选择一项`)
      }
      if (checked && maxCount && cloneOptions?.length >= maxCount) {
        return message.error(`${title}最多可选择${maxCount}项`)
      }
      if (checked) {
        if (multiple) {
          cloneOptions = Array.from(new Set([...cloneOptions, type]))
        } else {
          cloneOptions = [type]
        }
      } else {
        const index = cloneOptions.indexOf(type)
        if (index >= 0) {
          cloneOptions.splice(index, 1)
        }
      }
      set({
        selectedOptions: cloneOptions,
        checkAll: false,
      })
      // 数据change
      onChange && onChange(cloneOptions)
    },
    [title, unlimitedValue, selectedOptions, multiple, isMustChoice, maxCount, set, onChange],
  )

  // 赋值
  const setValues = useCallback(
    (values) => {
      set({
        selectedOptions: values,
        checkAll: values?.[0] === unlimitedValue,
      })
    },
    [unlimitedValue, set],
  )

  // 传递方法到父组件
  useImperativeHandle(ref, () => ({
    setValues,
  }))

  return (
    <>
      {elementType === "checkbox"
        ? options?.map((item: any) =>
            item[idField] === unlimitedValue ? (
              <Checkbox
                className={classNames("theme-checkbox-highlight", optionClass, {
                  "choice-active": checkAll,
                })}
                key={item[idField]}
                disabled={disabled}
                checked={checkAll}
                onChange={(event) => onCheckAllChange(event.target.checked)}
              >
                {unlimitedName}
              </Checkbox>
            ) : (
              <Checkbox
                className={classNames("theme-checkbox-highlight", optionClass, {
                  "choice-active": selectedOptions.indexOf(item[idField]) >= 0,
                })}
                key={item[idField]}
                disabled={disabled}
                checked={selectedOptions.indexOf(item[idField]) >= 0}
                onChange={(event) => optionsChange(item[idField], event.target.checked)}
              >
                {item[nameField]}
              </Checkbox>
            ),
          )
        : options?.map((item: any) =>
            item[idField] === unlimitedValue ? (
              <CheckableTag
                className={classNames(
                  "unlimited-choice-tag",
                  tagClassName,
                  optionClass,
                  {
                    "theme-tag-checkable-disabled": disabled,
                  },
                  {
                    "choice-active": checkAll,
                  },
                )}
                key={item[idField]}
                checked={checkAll}
                onChange={disabled ? undefined : (checked) => onCheckAllChange(checked)}
              >
                {unlimitedName}
              </CheckableTag>
            ) : (
              <CheckableTag
                className={classNames(
                  "unlimited-choice-tag",
                  tagClassName,
                  optionClass,
                  {
                    "theme-tag-checkable-disabled": disabled,
                  },
                  {
                    "choice-active": selectedOptions.indexOf(item[idField]) >= 0,
                  },
                )}
                key={item[idField]}
                checked={selectedOptions.indexOf(item[idField]) >= 0}
                onChange={disabled ? undefined : (checked) => optionsChange(item[idField], checked)}
              >
                {item[nameField]}
              </CheckableTag>
            ),
          )}
    </>
  )
}

export default forwardRef(UnlimitedChoice)
